import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';
import type { ChatMessage, ToolCallRecord } from '@innermate/shared';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso;
  }
}

const ROLE_TONE: Record<ChatMessage['role'], string> = {
  system: 'bg-surface-muted text-foreground-muted',
  user: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  assistant: 'bg-sky-500/10 text-sky-700 dark:text-sky-400',
  tool: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
};

export function AdminChatSessionDetail({ id }: { id: string }) {
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ['admin', 'chat-session', id],
    queryFn: () => api.adminGetChatSessionDetail(id),
  });

  return (
    <PageShell
      header={
        <PageHeader
          title={query.data?.title ?? '대화 상세'}
          description={
            query.data
              ? `${query.data.ownerDisplayName ?? ''} · ${query.data.ownerEmail ?? ''} · 메시지 ${query.data.messages.length}건`
              : '관리자 감사 열람 — 이 페이지 조회는 감사 로그에 기록됩니다.'
          }
          actions={
            <Button variant="ghost" onClick={() => navigate({ to: '/admin/saved-queries' })}>
              ← 저장된 쿼리로
            </Button>
          }
        />
      }
    >
      {query.isLoading ? (
        <div className="text-center text-foreground-muted py-10">불러오는 중…</div>
      ) : query.isError ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {query.error instanceof ApiError ? query.error.message : '세션을 불러오지 못했습니다.'}
        </div>
      ) : query.data ? (
        <>
          {query.data.deletedAt && (
            <div className="mb-4 rounded-2xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
              <strong>이 세션은 사용자가 삭제했습니다.</strong>{' '}
              <span className="text-foreground-muted">
                ({formatDateTime(query.data.deletedAt)}) — 사용자 화면에는 더 이상 노출되지 않지만
                메시지와 감사 기록은 보존됩니다.
              </span>
            </div>
          )}
          <div className="mb-4 grid grid-cols-2 gap-3 text-sm rounded-2xl border border-border bg-surface p-4">
            <Field k="세션 ID" mono>
              {query.data.id}
            </Field>
            <Field k="소유자 ID" mono>
              {query.data.ownerUserId}
            </Field>
            <Field k="생성">{formatDateTime(query.data.createdAt)}</Field>
            <Field k="마지막 업데이트">{formatDateTime(query.data.updatedAt)}</Field>
            {query.data.deletedAt ? (
              <Field k="삭제">{formatDateTime(query.data.deletedAt)}</Field>
            ) : null}
            {query.data.skillId ? (
              <Field k="Skill ID" mono>
                {query.data.skillId}
              </Field>
            ) : null}
          </div>

          <div className="space-y-3">
            {query.data.messages.length === 0 ? (
              <div className="text-center text-foreground-muted py-10 rounded-2xl border border-border bg-surface">
                메시지가 없습니다.
              </div>
            ) : (
              query.data.messages.map((m) => <MessageCard key={m.id} m={m} />)
            )}
          </div>
        </>
      ) : null}
    </PageShell>
  );
}

function MessageCard({ m }: { m: ChatMessage }) {
  return (
    <article className="rounded-2xl border border-border bg-surface p-4 text-sm">
      <header className="flex items-center gap-2 pb-2 border-b border-border mb-3">
        <span
          className={`inline-block rounded px-2 py-0.5 text-[11px] font-mono uppercase ${ROLE_TONE[m.role]}`}
        >
          {m.role}
        </span>
        <span className="text-xs text-foreground-muted font-mono">
          {formatDateTime(m.createdAt)}
        </span>
        {m.toolCallId ? (
          <span className="text-xs text-foreground-muted font-mono">· callId: {m.toolCallId}</span>
        ) : null}
      </header>
      {m.content ? (
        <pre className="font-mono text-[12px] whitespace-pre-wrap break-all bg-surface-muted/40 rounded p-3 overflow-x-auto">
          {m.content}
        </pre>
      ) : (
        <div className="text-foreground-muted text-xs">(내용 없음)</div>
      )}
      {m.attachedFileIds && m.attachedFileIds.length > 0 ? (
        <div className="mt-2 text-xs text-foreground-muted">
          첨부 파일: {m.attachedFileIds.join(', ')}
        </div>
      ) : null}
      {m.toolCalls && m.toolCalls.length > 0 ? (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs text-foreground-muted hover:text-foreground">
            툴 호출 {m.toolCalls.length}건 펼치기
          </summary>
          <div className="mt-2 space-y-2">
            {m.toolCalls.map((t) => (
              <ToolCallBlock key={t.id} t={t} />
            ))}
          </div>
        </details>
      ) : null}
    </article>
  );
}

function ToolCallBlock({ t }: { t: ToolCallRecord }) {
  return (
    <div className="rounded-md border border-border bg-surface-muted/30 p-3 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono font-medium">{t.toolKey}</span>
        <span
          className={`rounded px-1.5 py-0.5 text-[10px] font-mono uppercase ${
            t.status === 'ok'
              ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
              : 'bg-destructive/10 text-destructive'
          }`}
        >
          {t.status}
        </span>
        <span className="text-foreground-muted">{t.summary}</span>
        <span className="text-foreground-muted font-mono">· {t.latencyMs}ms</span>
      </div>
      <div className="mt-2">
        <div className="text-[10px] text-foreground-muted">input</div>
        <pre className="font-mono text-[11px] whitespace-pre-wrap break-all bg-surface rounded p-2 max-h-40 overflow-y-auto">
          {safeStringify(t.input)}
        </pre>
      </div>
      {t.output !== null ? (
        <div className="mt-2">
          <div className="text-[10px] text-foreground-muted">output</div>
          <pre className="font-mono text-[11px] whitespace-pre-wrap break-all bg-surface rounded p-2 max-h-40 overflow-y-auto">
            {safeStringify(t.output)}
          </pre>
        </div>
      ) : null}
      {t.error ? (
        <div className="mt-2">
          <div className="text-[10px] text-destructive">error</div>
          <pre className="font-mono text-[11px] whitespace-pre-wrap break-all text-destructive">
            {t.error}
          </pre>
        </div>
      ) : null}
    </div>
  );
}

function safeStringify(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

function Field({
  k,
  children,
  mono,
}: {
  k: string;
  children: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-xs text-foreground-muted w-28 shrink-0">{k}</span>
      <span className={mono ? 'font-mono text-xs break-all' : ''}>{children}</span>
    </div>
  );
}
